
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ProgressTracking } from '@/types/progress';

interface RobustProgressState {
  progress: ProgressTracking | null;
  isLoading: boolean;
  error: string | null;
  lastSync: Date | null;
  isOffline: boolean;
  retryCount: number;
}

export function useRobustProgressTracking(collectionId: string) {
  const [state, setState] = useState<RobustProgressState>({
    progress: null,
    isLoading: true,
    error: null,
    lastSync: null,
    isOffline: false,
    retryCount: 0
  });

  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const channelRef = useRef<any>(null);

  // Enhanced progress fetching with error recovery
  const fetchProgress = useCallback(async (isRetry = false) => {
    try {
      console.log(`🔄 Fetching progress for collection: ${collectionId} (retry: ${isRetry})`);
      
      if (!isRetry) {
        setState(prev => ({ ...prev, error: null, isLoading: true }));
      }

      const { data: progress, error } = await supabase
        .from('progress_tracking')
        .select('*')
        .eq('id', `upload-${collectionId}`)
        .maybeSingle();

      if (error) {
        console.error('Progress fetch error:', error);
        throw error;
      }

      setState(prev => ({
        ...prev,
        progress: progress as ProgressTracking,
        isLoading: false,
        error: null,
        lastSync: new Date(),
        isOffline: false,
        retryCount: 0
      }));

      console.log('✅ Progress fetched successfully:', progress);
      return progress;

    } catch (error) {
      console.error('Error fetching progress:', error);
      
      setState(prev => ({
        ...prev,
        error: error.message,
        isLoading: false,
        isOffline: true,
        retryCount: prev.retryCount + 1
      }));

      // Implement exponential backoff for retries
      if (state.retryCount < 5) {
        const retryDelay = Math.min(1000 * Math.pow(2, state.retryCount), 30000);
        console.log(`⏳ Retrying in ${retryDelay}ms (attempt ${state.retryCount + 1})`);
        
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
        
        retryTimeoutRef.current = setTimeout(() => {
          fetchProgress(true);
        }, retryDelay);
      }

      return null;
    }
  }, [collectionId, state.retryCount]);

  // Initialize progress tracking if needed
  const initializeProgress = useCallback(async () => {
    try {
      console.log(`🏗️ Initializing progress for collection: ${collectionId}`);
      
      const { error } = await supabase
        .from('progress_tracking')
        .upsert({
          id: `upload-${collectionId}`,
          collection_id: collectionId,
          total_items: 0,
          processed_items: 0,
          status: 'pending',
          current_stage: 'preparing',
          process_type: 'product_upload',
          notification_sent: false,
          retries: 0,
          updated_at: new Date().toISOString(),
          last_updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error initializing progress:', error);
        throw error;
      }

      console.log('✅ Progress initialized successfully');
      await fetchProgress();
      
    } catch (error) {
      console.error('Error initializing progress:', error);
      setState(prev => ({ ...prev, error: error.message }));
    }
  }, [collectionId, fetchProgress]);

  // Setup real-time subscription with reconnection logic
  const setupRealtimeSubscription = useCallback(() => {
    console.log('🔗 Setting up real-time subscription for:', collectionId);
    
    // Clean up existing channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    const channel = supabase
      .channel(`robust-progress-${collectionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'progress_tracking',
          filter: `id=eq.upload-${collectionId}`
        },
        (payload) => {
          console.log('📡 Real-time progress update:', payload);
          
          if (payload.new && payload.eventType !== 'DELETE') {
            const newProgress = payload.new as ProgressTracking;
            setState(prev => ({
              ...prev,
              progress: newProgress,
              lastSync: new Date(),
              isOffline: false,
              error: null,
              retryCount: 0
            }));
          } else if (payload.eventType === 'DELETE') {
            setState(prev => ({
              ...prev,
              progress: null,
              lastSync: new Date()
            }));
          }
        }
      )
      .subscribe((status) => {
        console.log('📡 Subscription status:', status);
        
        if (status === 'SUBSCRIBED') {
          setState(prev => ({ ...prev, isOffline: false }));
        } else if (status === 'CHANNEL_ERROR') {
          setState(prev => ({ ...prev, isOffline: true }));
          
          // Retry subscription after delay
          setTimeout(() => {
            setupRealtimeSubscription();
          }, 5000);
        }
      });

    channelRef.current = channel;
  }, [collectionId]);

  // Periodic sync to ensure data consistency
  const setupPeriodicSync = useCallback(() => {
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
    }

    syncIntervalRef.current = setInterval(() => {
      if (state.progress?.status === 'processing') {
        console.log('🔄 Periodic sync check');
        fetchProgress();
      }
    }, 10000); // Sync every 10 seconds during processing
  }, [fetchProgress, state.progress?.status]);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 App came online, syncing progress');
      setState(prev => ({ ...prev, isOffline: false }));
      fetchProgress();
    };

    const handleOffline = () => {
      console.log('📴 App went offline');
      setState(prev => ({ ...prev, isOffline: true }));
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !state.isOffline) {
        console.log('👁️ App became visible, checking progress');
        fetchProgress();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchProgress, state.isOffline]);

  // Main initialization effect
  useEffect(() => {
    fetchProgress();
    setupRealtimeSubscription();
    setupPeriodicSync();

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [collectionId]);

  // Update periodic sync when progress status changes
  useEffect(() => {
    setupPeriodicSync();
  }, [setupPeriodicSync]);

  // Force refresh function
  const forceRefresh = useCallback(() => {
    console.log('🔄 Force refresh triggered');
    setState(prev => ({ ...prev, retryCount: 0 }));
    fetchProgress();
  }, [fetchProgress]);

  return {
    ...state,
    fetchProgress,
    initializeProgress,
    forceRefresh,
    hasActiveProcessing: state.progress?.status === 'processing' || state.progress?.status === 'pending'
  };
}
